import { WcDatepicker } from 'wc-datepicker/dist/components/wc-datepicker';
customElements.define('wc-datepicker', WcDatepicker);

type FoldoutControl = (
	element: HTMLElement,
	closeAll: () => void,
) => { toggle: (force?: boolean) => void; isOpen: boolean };

const createDatePicker: FoldoutControl = (element, closeAll) => {
	const picker = element.querySelector('wc-datepicker');
	if (!picker) {
		throw new Error('wc-datepicker not found');
	}

	const today = new Date();
	const tomorrow = new Date();
	tomorrow.setDate(today.getDate() + 1);

	picker.value = [today, tomorrow];

	picker.disableDate = (date: Date) => {
		return date < new Date();
	};

	picker.labels = {
		clearButton: 'Töm värde',
		monthSelect: 'Välj månad',
		nextMonthButton: 'Nästa månad',
		nextYearButton: 'Nästa år',
		picker: 'Välj datum',
		previousMonthButton: 'Föregående månad',
		previousYearButton: 'Föregående år',
		todayButton: 'Visa idag',
		yearSelect: 'Välj år',
	};

	picker?.style.setProperty('display', 'none');

	let isOpen = false;

	const formatDate = (date: Date) => {
		const parts = [
			date.getFullYear(),
			String(date.getMonth() + 1).padStart(2, '0'),
			String(date.getDate()).padStart(2, '0'),
		];
		return parts.join('-');
	};

	const toggle = (force?: boolean) => {
		if (!picker) return;

		const shouldOpen = force ?? !isOpen;

		if (shouldOpen) {
			isOpen = true;
			picker.style.removeProperty('display');
			picker
				.querySelector<HTMLElement>('button, select, input, [tabindex="0"]')
				?.focus();
		} else {
			isOpen = false;
			picker.style.setProperty('display', 'none');
		}
	};

	const arrival = createDateRepresentation(
		element.querySelector('[data-date-representation="arrival"]')!,
	);
	const departure = createDateRepresentation(
		element.querySelector('[data-date-representation="departure"]')!,
	);

	const arrivalInput =
		element.querySelector<HTMLInputElement>('#edit-arrivaldate');
	const departureInput = element.querySelector<HTMLInputElement>(
		'#edit-departuredate',
	);

	const selectArrivalButton = element.querySelector<HTMLButtonElement>(
		'[data-select-arrival-date]',
	);

	const selectDepartureButton = element.querySelector<HTMLButtonElement>(
		'[data-select-departure-date]',
	);

	const updateDateRepresentation = (initial = false) => {
		if (!Array.isArray(picker.value)) {
			return;
		}

		const [arrivalDate, departureDate] = picker.value;

		arrival.setDate(arrivalDate);
		departure.setDate(departureDate || arrivalDate);

		arrivalInput?.setAttribute('value', formatDate(arrivalDate));
		departureInput?.setAttribute(
			'value',
			formatDate(departureDate || arrivalDate),
		);

		if (departureDate) {
			toggle(false);
			if (!initial) {
				selectDepartureButton?.focus();
			}
		}
	};

	updateDateRepresentation(true);

	picker?.addEventListener('selectDate', () => {
		updateDateRepresentation();
	});

	selectArrivalButton?.addEventListener('click', () => {
		if (isOpen) {
			closeAll();
		} else {
			closeAll();
			toggle(true);
		}
	});

	selectDepartureButton?.addEventListener('click', () => {
		if (isOpen) {
			closeAll();
		} else {
			closeAll();
			toggle(true);
		}
	});

	window.addEventListener('keydown', (event) => {
		if (isOpen && event.key === 'Escape') {
			toggle(false);
			selectArrivalButton?.focus();
		}
	});

	return {
		toggle,
		get isOpen() {
			return isOpen;
		},
	};
};

const createDateRepresentation = (element: HTMLElement) => {
	const monthNamesShort = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'Maj',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Okt',
		'Nov',
		'Dec',
	] as const;

	const top = element.querySelector('.top');
	const middle = element.querySelector('.middle');

	return {
		setDate(date: Date) {
			if (top) {
				top.textContent = monthNamesShort[date.getMonth()];
			}
			if (middle) {
				middle.textContent = String(date.getDate());
			}
		},
	};
};

const createAdultsPicker: FoldoutControl = (element, closeAll) => {
	const adultsPickerButton = element.querySelector('button');
	const adultsPickerOptions = element.querySelectorAll<HTMLElement>(
		'[data-adult-option]',
	);
	const valueElement = element.querySelector<HTMLElement>('button .middle');

	const adultsInput = element.querySelector<HTMLInputElement>('#edit-adults');

	let isOpen = false;

	const toggle = (force?: boolean) => {
		isOpen = element.classList.toggle('picker-open', force);
	};

	adultsPickerButton?.addEventListener('click', () => {
		if (isOpen) {
			closeAll();
		} else {
			closeAll();
			toggle(true);
		}
		adultsPickerOptions[0]?.focus();
	});

	adultsPickerOptions?.forEach((option) => {
		option.addEventListener('click', () => {
			const number = Number(option.getAttribute('data-adult-option'));
			if (valueElement) {
				valueElement.textContent = String(number);
				adultsInput?.setAttribute('value', String(number));
				toggle(false);
				adultsPickerButton?.focus();
			}
		});
	});

	window.addEventListener('keydown', (event) => {
		if (isOpen && event.key === 'Escape') {
			toggle(false);
			adultsPickerButton?.focus();
		}
	});

	return {
		toggle,
		get isOpen() {
			return isOpen;
		},
	};
};

const createMoreOptions: FoldoutControl = (element, closeAll) => {
	const moreOptionsButton = element.querySelector('.more-options-toggle');

	let isOpen = false;

	const toggle = (force?: boolean) => {
		const bookingMask = element.closest('#shcustom-bookingmask-form');
		if (bookingMask) {
			isOpen = bookingMask.classList.toggle('more-options-open', force);
		}
	};

	moreOptionsButton?.addEventListener('click', (event) => {
		if (isOpen) {
			closeAll();
		} else {
			closeAll();
			toggle(true);
		}
	});

	return {
		toggle,
		get isOpen() {
			return isOpen;
		},
	};
};

const createBookingWidget = (element: HTMLElement) => {
	const controls = new Set<ReturnType<FoldoutControl>>();

	const closeAll = () => {
		controls.forEach((control) => {
			if (control.isOpen) {
				control.toggle(false);
			}
		});
	};

	controls.add(createDatePicker(element, closeAll));

	const adultsPickerElement = element.querySelector<HTMLElement>(
		'[data-adults-picker]',
	);
	if (adultsPickerElement) {
		controls.add(createAdultsPicker(adultsPickerElement, closeAll));
	}

	const moreOptionsElement = element.querySelector<HTMLElement>(
		'[data-more-options]',
	);
	if (moreOptionsElement) {
		controls.add(createMoreOptions(moreOptionsElement, closeAll));
	}

	document.body.addEventListener('click', (event) => {
		if (!(event.target instanceof Element)) return;

		if (element.contains(event.target)) {
			return;
		}

		controls.forEach((control) => {
			control.toggle(false);
		});
	});
};

document.addEventListener('DOMContentLoaded', function () {
	document
		.querySelectorAll<HTMLElement>('[data-booking-widget]')
		.forEach(createBookingWidget);
});
